import { useCallback } from 'react';
import { useSyncStore } from '../store/syncStore';
import Actions from '../store/Actions';
import { showSnackbar } from '../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../context/Snackbar/Types';

export const useSyncOperations = () => {
  const {
    isSyncingPortfolioAndCustodian,
    setSyncingPortfolioAndCustodian,
    isSyncingOTCDatabase,
    setSyncingOTCDatabase,
  } = useSyncStore();

  const syncPortfolioCustodianFromIllio = useCallback(
    async (onSuccess?: () => void) => {
      try {
        setSyncingPortfolioAndCustodian(true);

        const result = await Actions.syncPortfolioCustodianFromIllio();

        if (!result.errors || result.errors.length === 0) {
          showSnackbar({
            title: 'Success',
            message:
              result.message ||
              `Sync completed. ${result.records_added || 0} added, ${result.records_updated || 0} updated, ${result.records_deleted || 0} deleted.`,
            type: SnackbarType.success,
          });

          // Call onSuccess callback if provided
          if (onSuccess) {
            await onSuccess();
          }
        } else {
          showSnackbar({
            title: 'Warning',
            message: result.message || 'Sync completed with some issues',
            type: SnackbarType.warning,
          });
          // Still call onSuccess to refresh data even with warnings
          if (onSuccess) {
            await onSuccess();
          }
        }

        if (result.errors && result.errors.length > 0) {
          console.warn('Sync errors:', result.errors);
        }
      } catch (error) {
        showSnackbar({
          title: 'Error',
          message: 'Failed to sync portfolios and custodians from Illio',
          type: SnackbarType.error,
        });
        console.error('Sync error:', error);
      } finally {
        setSyncingPortfolioAndCustodian(false);
      }
    },
    [setSyncingPortfolioAndCustodian]
  );

  const syncOTCDatabaseFromIllio = useCallback(
    async (onSuccess?: () => void) => {
      try {
        setSyncingOTCDatabase(true);

        const result = await Actions.syncOTCDatabaseFromIllio();

        if (result.success) {
          showSnackbar({
            title: 'Success',
            message:
              result.message ||
              `Sync completed. ${result.synced_count || 0} synced.`,
            type: SnackbarType.success,
          });

          if (onSuccess) {
            await onSuccess();
          }
        } else {
          showSnackbar({
            title: 'Warning',
            message: result.message || 'Sync completed with some issues',
            type: SnackbarType.warning,
          });
          // Still refresh data
          if (onSuccess) {
            await onSuccess();
          }
        }

        if (result.errors && result.errors.length > 0) {
          console.warn('OTC Database sync errors:', result.errors);
        }
      } catch (error) {
        showSnackbar({
          title: 'Error',
          message: 'Failed to sync OTC database from Illio',
          type: SnackbarType.error,
        });
        console.error('OTC Database sync error:', error);
      } finally {
        setSyncingOTCDatabase(false);
      }
    },
    [setSyncingOTCDatabase]
  );

  return {
    isSyncingPortfolioAndCustodian,
    syncPortfolioCustodianFromIllio,
    isSyncingOTCDatabase,
    syncOTCDatabaseFromIllio,
  };
};
